/**
 * Share Graphic Generator
 *
 * Generates Instagram story-sized images (1080x1920) for sharing competitions.
 * Uses HTML5 Canvas for client-side generation.
 */

// Instagram story dimensions
const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1920;

// Colors from design system
const COLORS = {
  midnight: '#0D1117',
  midnightLight: '#161B22',
  cream: '#F5F0E8',
  creamMuted: 'rgba(245, 240, 232, 0.7)',
  primary: '#14B8A6',
  primaryDark: '#0D9488',
};

/**
 * Load an image from URL
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Draw rounded rectangle
 */
function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Draw the Raflr logo (ticket stub with R)
 */
function drawLogo(ctx, x, y, size) {
  const scale = size / 32;

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);

  // Ticket stub shape
  ctx.fillStyle = COLORS.primary;
  ctx.beginPath();
  ctx.moveTo(6, 4);
  ctx.lineTo(26, 4);
  ctx.quadraticCurveTo(28, 4, 28, 6);
  ctx.lineTo(28, 12);
  ctx.bezierCurveTo(26.3, 12, 25, 13.3, 25, 15);
  ctx.bezierCurveTo(25, 16.7, 26.3, 18, 28, 18);
  ctx.lineTo(28, 26);
  ctx.quadraticCurveTo(28, 28, 26, 28);
  ctx.lineTo(6, 28);
  ctx.quadraticCurveTo(4, 28, 4, 26);
  ctx.lineTo(4, 18);
  ctx.bezierCurveTo(5.7, 18, 7, 16.7, 7, 15);
  ctx.bezierCurveTo(7, 13.3, 5.7, 12, 4, 12);
  ctx.lineTo(4, 6);
  ctx.quadraticCurveTo(4, 4, 6, 4);
  ctx.closePath();
  ctx.fill();

  // R letter
  ctx.fillStyle = 'white';
  ctx.font = 'bold 16px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('R', 16, 16);

  ctx.restore();
}

/**
 * Wrap text to fit within a max width
 */
function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);

    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Format currency
 */
function formatPrice(cents) {
  return `€${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

/**
 * Format date
 */
function formatEndDate(date) {
  const endDate = date instanceof Date ? date : date.toDate();
  const now = new Date();
  const diff = endDate - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} left`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} left`;
  } else {
    return 'Ending soon!';
  }
}

/**
 * Generate share graphic for a competition
 *
 * @param {Object} raffle - The raffle/competition data
 * @param {string} raffle.title - Competition title
 * @param {string[]} raffle.photos - Array of photo URLs
 * @param {number} raffle.ticketPrice - Entry price in cents
 * @param {number} raffle.ticketQuantity - Total entries available
 * @param {number} raffle.ticketsSold - Entries sold so far
 * @param {Date|Timestamp} raffle.endsAt - End date
 * @param {string} raffle.id - Competition ID for URL
 * @returns {Promise<string>} - Data URL of the generated image
 */
export async function generateShareGraphic(raffle) {
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;
  const ctx = canvas.getContext('2d');

  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
  gradient.addColorStop(0, COLORS.midnight);
  gradient.addColorStop(1, COLORS.midnightLight);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

  // Decorative elements - subtle grid pattern
  ctx.strokeStyle = 'rgba(245, 240, 232, 0.03)';
  ctx.lineWidth = 1;
  for (let i = 0; i < CANVAS_WIDTH; i += 60) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i, CANVAS_HEIGHT);
    ctx.stroke();
  }
  for (let i = 0; i < CANVAS_HEIGHT; i += 60) {
    ctx.beginPath();
    ctx.moveTo(0, i);
    ctx.lineTo(CANVAS_WIDTH, i);
    ctx.stroke();
  }

  // Top section - Logo and branding
  const logoSize = 64;
  drawLogo(ctx, 60, 80, logoSize);

  ctx.fillStyle = COLORS.cream;
  ctx.font = 'bold 48px Inter, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('raflr', 140, 112);

  // Main photo area (with rounded corners)
  const photoY = 200;
  const photoSize = 800;
  const photoX = (CANVAS_WIDTH - photoSize) / 2;

  // Photo shadow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 20;

  // Draw photo background/placeholder
  ctx.fillStyle = COLORS.midnightLight;
  roundRect(ctx, photoX, photoY, photoSize, photoSize, 24);
  ctx.fill();

  // Reset shadow
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // Load and draw main photo
  if (raffle.photos && raffle.photos.length > 0) {
    try {
      const img = await loadImage(raffle.photos[0]);

      // Create clipping path for rounded corners
      ctx.save();
      roundRect(ctx, photoX, photoY, photoSize, photoSize, 24);
      ctx.clip();

      // Calculate cover fit
      const imgRatio = img.width / img.height;
      let drawWidth, drawHeight, drawX, drawY;

      if (imgRatio > 1) {
        // Landscape
        drawHeight = photoSize;
        drawWidth = photoSize * imgRatio;
        drawX = photoX - (drawWidth - photoSize) / 2;
        drawY = photoY;
      } else {
        // Portrait or square
        drawWidth = photoSize;
        drawHeight = photoSize / imgRatio;
        drawX = photoX;
        drawY = photoY - (drawHeight - photoSize) / 2;
      }

      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
      ctx.restore();
    } catch (error) {
      console.error('Failed to load image:', error);
    }
  }

  // Price badge (top right of photo)
  const badgeWidth = 200;
  const badgeHeight = 80;
  const badgeX = photoX + photoSize - badgeWidth - 20;
  const badgeY = photoY + 20;

  ctx.fillStyle = COLORS.primary;
  roundRect(ctx, badgeX, badgeY, badgeWidth, badgeHeight, 16);
  ctx.fill();

  ctx.fillStyle = 'white';
  ctx.font = 'bold 40px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(formatPrice(raffle.ticketPrice), badgeX + badgeWidth / 2, badgeY + badgeHeight / 2);

  ctx.font = '20px Inter, system-ui, sans-serif';
  ctx.fillText('per entry', badgeX + badgeWidth / 2, badgeY + badgeHeight / 2 + 28);

  // Content section below photo
  const contentY = photoY + photoSize + 60;

  // Title
  ctx.fillStyle = COLORS.cream;
  ctx.font = 'bold 56px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';

  const titleLines = wrapText(ctx, raffle.title, CANVAS_WIDTH - 120);
  titleLines.slice(0, 2).forEach((line, index) => {
    ctx.fillText(line, CANVAS_WIDTH / 2, contentY + index * 70);
  });

  // Stats row
  const statsY = contentY + titleLines.length * 70 + 40;

  // Entries available
  ctx.fillStyle = COLORS.creamMuted;
  ctx.font = '32px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';

  const entriesLeft = raffle.ticketQuantity - (raffle.ticketsSold || 0);
  const statsText = `${entriesLeft} entries available  •  ${formatEndDate(raffle.endsAt)}`;
  ctx.fillText(statsText, CANVAS_WIDTH / 2, statsY);

  // CTA Button
  const ctaY = statsY + 100;
  const ctaWidth = 500;
  const ctaHeight = 100;
  const ctaX = (CANVAS_WIDTH - ctaWidth) / 2;

  // Button gradient
  const ctaGradient = ctx.createLinearGradient(ctaX, ctaY, ctaX + ctaWidth, ctaY);
  ctaGradient.addColorStop(0, COLORS.primary);
  ctaGradient.addColorStop(1, COLORS.primaryDark);
  ctx.fillStyle = ctaGradient;
  roundRect(ctx, ctaX, ctaY, ctaWidth, ctaHeight, 50);
  ctx.fill();

  ctx.fillStyle = 'white';
  ctx.font = 'bold 36px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Enter Now', ctaX + ctaWidth / 2, ctaY + ctaHeight / 2);

  // Bottom section - URL
  const urlY = CANVAS_HEIGHT - 120;

  ctx.fillStyle = COLORS.creamMuted;
  ctx.font = '36px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('raflr.ie', CANVAS_WIDTH / 2, urlY);

  // Swipe up indicator
  ctx.fillStyle = COLORS.creamMuted;
  ctx.font = '28px Inter, system-ui, sans-serif';
  ctx.fillText('Swipe up to enter', CANVAS_WIDTH / 2, urlY + 50);

  // Arrow up icon
  ctx.strokeStyle = COLORS.creamMuted;
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(CANVAS_WIDTH / 2, urlY - 50);
  ctx.lineTo(CANVAS_WIDTH / 2 - 15, urlY - 35);
  ctx.moveTo(CANVAS_WIDTH / 2, urlY - 50);
  ctx.lineTo(CANVAS_WIDTH / 2 + 15, urlY - 35);
  ctx.stroke();

  return canvas.toDataURL('image/png');
}

/**
 * Download the generated image
 */
export function downloadShareGraphic(dataUrl, filename = 'raflr-competition.png') {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  link.click();
}

/**
 * Share using Web Share API if available
 */
export async function shareGraphic(dataUrl, title) {
  // Convert data URL to blob
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const file = new File([blob], 'raflr-competition.png', { type: 'image/png' });

  if (navigator.share && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        title: title || 'Check out this competition on Raflr!',
        files: [file],
      });
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share failed:', error);
      }
      return false;
    }
  }

  // Fallback to download
  downloadShareGraphic(dataUrl);
  return false;
}
